import React, { useEffect, useState, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TelegramProvider, useTelegram } from "@/contexts/TelegramContext";
import { TurnstileProvider } from "@/contexts/TurnstileContext";
import { ConfigurationProvider } from "@/contexts/ConfigurationContext";
import { HeaderProvider } from "@/contexts/HeaderContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// PostHog now initializes lazily (dynamic import) after idle or first interaction
import { initPostHog } from "@/integrations/posthog";
import { isTelegramMockEnabled } from "@/lib/telegramMock";
import { offerApi } from "@/api/database";
import { useLanguageSync } from "@/hooks/useLanguageSync";
import { useUserPresence } from "@/hooks/useUserPresence";

// -------------------------------------------
// Lazy Page Components (grouped by domain)
// -------------------------------------------
// Public
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
const MyOffersPage = lazy(() => import("./pages/MyOffersPage"));
const ContactsPage = lazy(() => import("./pages/ContactsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const OfferDetail = lazy(() => import("./pages/OfferDetail"));
const PublicProfile = lazy(() =>
  import("./pages/PublicProfile").then((m) => ({ default: m.PublicProfile })),
);
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const AuthErrorScreen = lazy(() => import("./pages/AuthErrorScreen"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RoleSelectionPage = lazy(() => import("./pages/auth/RoleSelectionPage"));
const DashboardRedirect = lazy(() => import("./pages/DashboardRedirect"));

// Protected
const CreateOfferPage = lazy(() => import("./pages/CreateOfferPage"));
const BookmarkedOffers = lazy(() => import("./pages/BookmarkedOffers"));
const TestimonialsPage = lazy(() =>
  import("./pages/TestimonialsPage").then((m) => ({
    default: m.TestimonialsPage,
  })),
);
const LeaderboardsPage = lazy(() => import("./pages/LeaderboardsPage"));
const AnalyticsTest = lazy(() => import("./pages/AnalyticsTest"));
const TicketsPage = lazy(() => import("./pages/TicketsPage"));
const TicketDetailPage = lazy(() => import("./pages/TicketDetailPage"));
const ProfilePage = lazy(() => import("./modules/profile/ProfilePage"));
const ProfileEditPage = lazy(() => import("./pages/ProfileEditPage"));
const ChatsPage = lazy(() => import("./pages/ChatsPage"));
const ChatPageAdaptive = lazy(() => import("./pages/ChatPageAdaptive"));
const NewsPage = lazy(() => import("./pages/NewsPage"));

// Admin
const SecureRoute = lazy(() => import("./components/SecureRoute"));
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminAnalyticsPage = lazy(
  () => import("./pages/admin/AdminAnalyticsPage"),
);
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const UserManagementPage = lazy(
  () => import("./pages/admin/UserManagementPage"),
);
const UserDetailsPage = lazy(() => import("./pages/admin/UserDetailsPage"));
const AdminReferralsPage = lazy(
  () => import("./pages/admin/AdminReferralsPage"),
);
const ChatModeration = lazy(() => import("./pages/ChatModeration"));
const AdminTestimonialsPage = lazy(() =>
  import("./pages/AdminTestimonialsPage").then((m) => ({
    default: m.AdminTestimonialsPage,
  })),
);
const AdminAnnouncementsPage = lazy(
  () => import("./pages/admin/AdminAnnouncementsPage"),
);
const AdminSettingsPage = lazy(() => import("./pages/admin/AdminSettingsPage"));

// Shared
const LoadingScreen = lazy(() => import("./components/LoadingScreen"));
const ProfileAccessGuard = lazy(
  () => import("./components/ProfileAccessGuard"),
);
const PushNotificationPrompt = lazy(
  () => import("./components/PushNotificationPrompt"),
);
// const ReloadPrompt = lazy(() => import("./components/ReloadPrompt").then(m => ({ default: m.ReloadPrompt }))); // Disabled: vite-plugin-pwa removed

// Critical (not lazy) to avoid auth race in mock mode
import { AuthHandler } from "./components/AuthHandler";
import { TelegramPlatformGuard } from "./components/TelegramPlatformGuard";
import PrimarySidebar, { SidebarShell } from "@/components/PrimarySidebar";
import PrimaryHeader from "@/components/PrimaryHeader";
import { SidebarInset } from "@/components/ui/sidebar";
// -------------------------------------------
// Query Client Singleton (behavior unchanged)
// Optional defaultOptions can be enabled later; kept minimal now.
// -------------------------------------------
// -------------------------------------------
// Query Client with Global Auth Error Handling
// -------------------------------------------
let globalNavigate:
  | ((path: string, options?: { replace?: boolean }) => void)
  | null = null;

// Helper function to check if error is auth-related
const isAuthError = (error: unknown): boolean => {
  if (!error) return false;

  // Check PostgrestError from D1 Database
  if (typeof error === "object" && error !== null) {
    const err = error as Record<string, unknown>;

    // Supabase auth errors
    if (err.status === 401 || err.status === 403) return true;
    if (err.code === "PGRST301" || err.code === "PGRST302") return true; // JWT errors

    // Check message for auth-related keywords
    const message = err.message || err.error_description || "";
    if (typeof message === "string") {
      const authKeywords = [
        "jwt",
        "unauthorized",
        "forbidden",
        "token",
        "session",
        "authentication",
      ];
      return authKeywords.some((keyword) =>
        message.toLowerCase().includes(keyword),
      );
    }
  }

  return false;
};

// Global error handler for authentication errors
const handleGlobalAuthError = (error: unknown) => {
  if (!isAuthError(error)) return;

  console.warn("[QueryClient] Auth error detected, signing out:", error);

  // Sign out the user via Workers Auth
  import("@/integrations/database/client")
    .then(({ workersClient }) => workersClient.auth.getSession())
    .then(() => {
      console.log("[QueryClient] User signed out due to auth error");

      // Navigate to auth error page if navigation is available
      if (globalNavigate) {
        globalNavigate("/auth-error", { replace: true });
      }
    })
    .catch((signOutError) => {
      console.error(
        "[QueryClient] Error during auth error sign out:",
        signOutError,
      );
    });
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable noisy refetch-on-focus globally; opt-in per query if needed.
      refetchOnWindowFocus: false,
      // Conservative retry; keep 3 for critical ones explicitly.
      retry: (failureCount, error) => {
        // Don't retry auth errors
        if (isAuthError(error)) {
          handleGlobalAuthError(error);
          return false;
        }
        // Default retry logic for other errors
        return failureCount < 2;
      },
      // Keep staleTime default (0) unless specified at call-site to avoid surprising stale views.
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry auth errors
        if (isAuthError(error)) {
          handleGlobalAuthError(error);
          return false;
        }
        // Default retry logic for other errors
        return failureCount < 1;
      },
    },
  },
});

// -------------------------------------------
// Constants
// -------------------------------------------
const MOCK_FORCE_AUTH_DELAY_MS = 1200;

// -------------------------------------------
// Local Error Boundary for lazy route chunk failures
// -------------------------------------------
interface RouteErrorBoundaryState {
  hasError: boolean;
  message?: string;
  isChunkError: boolean;
  autoReloadAttempted: boolean;
}
interface RouteErrorBoundaryProps {
  children: React.ReactNode;
}

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
  /ChunkLoadError/i,
  /Loading chunk \d+ failed/i,
  /Failed to import/i,
];

const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));
};

class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  private autoReloadTimer?: number;

  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      isChunkError: false,
      autoReloadAttempted: false,
    };
  }

  static getDerivedStateFromError(err: unknown): RouteErrorBoundaryState {
    const isChunk = isChunkLoadError(err);
    return {
      hasError: true,
      message: err instanceof Error ? err.message : "Unknown error",
      isChunkError: isChunk,
      autoReloadAttempted: false,
    };
  }

  componentDidCatch(error: unknown, _info: unknown) {
    console.error("[RouteErrorBoundary] Caught error:", error);

    // Автоматичне перезавантаження при chunk loading errors
    if (isChunkLoadError(error) && !this.state.autoReloadAttempted) {
      this.attemptAutoReload();
    }
  }

  componentWillUnmount() {
    if (this.autoReloadTimer) {
      clearTimeout(this.autoReloadTimer);
    }
  }

  attemptAutoReload = async () => {
    const reloadKey = "chunk_error_reload_count";
    const reloadTimestampKey = "chunk_error_last_reload";
    const maxReloads = 3; // Увеличиваем количество попыток
    const reloadCooldown = 5000; // Уменьшаем cooldown до 5 секунд

    try {
      const reloadCount = parseInt(
        sessionStorage.getItem(reloadKey) || "0",
        10,
      );
      const lastReload = parseInt(
        sessionStorage.getItem(reloadTimestampKey) || "0",
        10,
      );
      const now = Date.now();

      // Перевіряємо cooldown
      if (now - lastReload < reloadCooldown) {
        console.warn(
          "[RouteErrorBoundary] Reload cooldown active, skipping auto-reload",
        );
        this.setState({ autoReloadAttempted: true });
        return;
      }

      if (reloadCount < maxReloads) {
        console.log(
          `[RouteErrorBoundary] Auto-reloading (attempt ${reloadCount + 1}/${maxReloads})`,
        );

        this.setState({ autoReloadAttempted: true });

        // Очищаємо всі кеші включно з Service Workers
        if ("caches" in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
          console.log(
            "[RouteErrorBoundary] Cleared",
            cacheNames.length,
            "cache(s)",
          );
        }

        // Очищаємо Service Workers
        if ("serviceWorker" in navigator) {
          const registrations =
            await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((reg) => reg.unregister()));
          if (registrations.length > 0) {
            console.log(
              "[RouteErrorBoundary] Unregistered",
              registrations.length,
              "service worker(s)",
            );
          }
        }

        // Оновлюємо лічильники
        sessionStorage.setItem(reloadKey, String(reloadCount + 1));
        sessionStorage.setItem(reloadTimestampKey, String(now));

        // Затримка перед перезавантаженням для показу повідомлення
        this.autoReloadTimer = window.setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        console.error("[RouteErrorBoundary] Max reload attempts reached");
        this.setState({ autoReloadAttempted: true });
      }
    } catch (err) {
      console.error("[RouteErrorBoundary] Error during auto-reload:", err);
      this.setState({ autoReloadAttempted: true });
    }
  };

  handleManualReload = async () => {
    // Очищаємо всі кеші та лічильники
    sessionStorage.clear();

    // Очищаємо HTTP кеші
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }

    // Очищаємо Service Workers
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }

    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Показуємо повідомлення про автоматичне перезавантаження для chunk errors
      if (this.state.isChunkError && !this.state.autoReloadAttempted) {
        return (
          <div className="flex flex-col items-center justify-center w-full h-screen gap-4 p-6 text-center">
            <div className="text-blue-500">
              <svg
                className="w-16 h-16 mx-auto mb-4 animate-spin"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <p className="text-lg font-semibold">Оновлення додатку...</p>
              <p className="mt-2 text-sm opacity-70">
                Завантажуємо нову версію
              </p>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Виявлено нову версію. Сторінка автоматично перезавантажується...
            </p>
          </div>
        );
      }

      // Стандартний екран помилки з можливістю ручного перезавантаження
      return (
        <div className="flex flex-col items-center justify-center w-full h-screen gap-4 p-6 text-center">
          <div className="text-red-500">
            <svg
              className="w-16 h-16 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-lg font-semibold">
              Помилка завантаження сторінки
            </p>
            <p className="mt-2 text-sm opacity-70 font-mono text-xs">
              {this.state.message}
            </p>
          </div>
          <p className="text-sm text-muted-foreground max-w-md">
            {this.state.isChunkError
              ? "Не вдалося завантажити оновлену версію додатку. Спробуйте перезавантажити сторінку."
              : "Сталася помилка під час завантаження сторінки. Спробуйте перезавантажити."}
          </p>
          <button
            onClick={this.handleManualReload}
            className="px-6 py-3 text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Перезавантажити сторінку
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// -------------------------------------------
// Routes Component (grouped)
// -------------------------------------------
const AppRoutes = (): JSX.Element => (
  <Routes>
    {/* Public */}
    <Route
      path="/"
      element={
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-screen text-xs text-muted-foreground">
              Loading Traficomatic...
            </div>
          }
        >
          <MarketplacePage />
        </Suspense>
      }
    />
    <Route
      path="/offer/:id"
      element={
        <Suspense
          fallback={<div className="p-6 text-sm">Loading offer...</div>}
        >
          <OfferDetail />
        </Suspense>
      }
    />
    <Route
      path="/u/:profileNickname"
      element={
        <Suspense
          fallback={<div className="p-6 text-sm">Loading profile...</div>}
        >
          <PublicProfile />
        </Suspense>
      }
    />
    <Route
      path="/dashboard"
      element={
        <Suspense fallback={<div className="p-6 text-sm">Redirecting...</div>}>
          <DashboardRedirect />
        </Suspense>
      }
    />

    {/* Auth Flow */}
    <Route
      path="/role-selection"
      element={
        <Suspense
          fallback={
            <div className="p-6 text-sm">Loading role selection...</div>
          }
        >
          <RoleSelectionPage />
        </Suspense>
      }
    />
    <Route
      path="/onboarding"
      element={
        <Suspense
          fallback={<div className="p-6 text-sm">Loading onboarding...</div>}
        >
          <OnboardingPage />
        </Suspense>
      }
    />
    <Route
      path="/auth-error"
      element={
        <Suspense fallback={<div className="p-6 text-sm">Auth state...</div>}>
          <AuthErrorScreen />
        </Suspense>
      }
    />

    {/* Protected cluster */}
    <Route element={<SecureRoute />}>
      <Route
        path="/create-offer"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Preparing editor...</div>}
          >
            <CreateOfferPage />
          </Suspense>
        }
      />
      <Route
        path="/profile"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading profile...</div>}
          >
            <ProfileAccessGuard>
              <ProfilePage />
            </ProfileAccessGuard>
          </Suspense>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading editor...</div>}
          >
            <ProfileAccessGuard>
              <ProfileEditPage />
            </ProfileAccessGuard>
          </Suspense>
        }
      />
      <Route
        path="/chats"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading chats...</div>}
          >
            <ChatsPage />
          </Suspense>
        }
      />
      <Route
        path="/chat/:id"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading chat...</div>}
          >
            <ChatPageAdaptive />
          </Suspense>
        }
      />
      <Route
        path="/bookmarks"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading bookmarks...</div>}
          >
            <BookmarkedOffers />
          </Suspense>
        }
      />
      <Route
        path="/testimonials"
        element={
          <Suspense
            fallback={
              <div className="p-4 text-sm">Loading testimonials...</div>
            }
          >
            <TestimonialsPage />
          </Suspense>
        }
      />
      <Route
        path="/leaderboards"
        element={
          <Suspense
            fallback={
              <div className="p-4 text-sm">Loading leaderboards...</div>
            }
          >
            <LeaderboardsPage />
          </Suspense>
        }
      />
      <Route
        path="/tickets"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading tickets...</div>}
          >
            <TicketsPage />
          </Suspense>
        }
      />
      <Route
        path="/tickets/:id"
        element={
          <Suspense
            fallback={
              <div className="p-4 text-sm">Loading ticket details...</div>
            }
          >
            <TicketDetailPage />
          </Suspense>
        }
      />
      <Route
        path="/my-offers"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading my offers...</div>}
          >
            <MyOffersPage />
          </Suspense>
        }
      />
      <Route
        path="/contacts"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading contacts...</div>}
          >
            <ContactsPage />
          </Suspense>
        }
      />
      <Route
        path="/settings"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading settings...</div>}
          >
            <SettingsPage />
          </Suspense>
        }
      />
      <Route
        path="/news"
        element={
          <Suspense
            fallback={<div className="p-4 text-sm">Loading news...</div>}
          >
            <NewsPage />
          </Suspense>
        }
      />
    </Route>

    {/* Admin cluster */}
    <Route
      path="/admin"
      element={
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-screen text-sm">
              Admin shell...
            </div>
          }
        >
          <AdminLayout />
        </Suspense>
      }
    >
      <Route
        path="dashboard"
        element={
          <Suspense
            fallback={
              <div className="p-4 text-xs">Loading admin dashboard...</div>
            }
          >
            <AdminDashboard />
          </Suspense>
        }
      />
      <Route
        path="analytics"
        element={
          <Suspense
            fallback={<div className="p-4 text-xs">Loading analytics...</div>}
          >
            <AdminAnalyticsPage />
          </Suspense>
        }
      />
      <Route
        path="users"
        element={
          <Suspense
            fallback={<div className="p-4 text-xs">Loading users...</div>}
          >
            <UserManagementPage />
          </Suspense>
        }
      />
      <Route
        path="users/:userId"
        element={
          <Suspense
            fallback={
              <div className="p-4 text-xs">Loading user details...</div>
            }
          >
            <UserDetailsPage />
          </Suspense>
        }
      />
      <Route
        path="referrals"
        element={
          <Suspense
            fallback={<div className="p-4 text-xs">Loading referrals...</div>}
          >
            <AdminReferralsPage />
          </Suspense>
        }
      />
      <Route
        path="chats"
        element={
          <Suspense
            fallback={
              <div className="p-4 text-xs">Loading chat moderation...</div>
            }
          >
            <ChatModeration />
          </Suspense>
        }
      />
      <Route
        path="testimonials"
        element={
          <Suspense
            fallback={
              <div className="p-4 text-xs">
                Loading testimonials moderation...
              </div>
            }
          >
            <AdminTestimonialsPage />
          </Suspense>
        }
      />
      <Route
        path="announcements"
        element={
          <Suspense
            fallback={
              <div className="p-4 text-xs">Loading announcements...</div>
            }
          >
            <AdminAnnouncementsPage />
          </Suspense>
        }
      />
      <Route
        path="settings"
        element={
          <Suspense
            fallback={<div className="p-4 text-xs">Loading settings...</div>}
          >
            <AdminSettingsPage />
          </Suspense>
        }
      />
      <Route
        path="analytics-test"
        element={
          <Suspense
            fallback={
              <div className="p-4 text-xs">Loading analytics test...</div>
            }
          >
            <AnalyticsTest />
          </Suspense>
        }
      />
      <Route
        index
        element={
          <Suspense
            fallback={<div className="p-4 text-xs">Loading admin...</div>}
          >
            <AdminDashboard />
          </Suspense>
        }
      />
    </Route>

    {/* Fallback */}
    <Route
      path="*"
      element={
        <Suspense fallback={<div className="p-6 text-sm">Resolving...</div>}>
          <NotFound />
        </Suspense>
      }
    />
  </Routes>
);

// -------------------------------------------
// AuthController - governs post-auth navigation sequencing
// -------------------------------------------
const AuthController: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { session, userProfile, appLoading, authError, authStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Register global navigate function for QueryClient error handling
  useEffect(() => {
    globalNavigate = navigate;
    return () => {
      globalNavigate = null;
    };
  }, [navigate]);

  // Публічні роуты, які не вимагають аутентифікації
  const publicRoutes = ["/role-selection", "/auth-error"];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  useEffect(() => {
    // Пропускаем публичные роуты
    if (isPublicRoute) {
      return;
    }

    // В design mode пропускаем все проверки аутентификации
    // Если есть ошибка аутентификации - редиректим на страницу ошибки
    if (authError && location.pathname !== "/auth-error") {
      console.log(
        "[AuthController] ❌ Auth error detected, redirecting to /auth-error",
      );
      navigate("/auth-error", { replace: true });
      return;
    }

    // Если все еще загружается - ждем
    if (appLoading) {
      console.log("[AuthController] ⏳ App still loading...", { authStatus });
      return;
    }

    // Если есть сессия и профиль - проверяем роль
    if (session && userProfile) {
      console.log("[AuthController] ✅ Session and profile available", {
        role: userProfile.role,
        pathname: location.pathname,
        authStatus,
      });

      // Если нет роли - отправляем на onboarding
      if (userProfile.role == null) {
        if (location.pathname !== "/onboarding") {
          console.log(
            "[AuthController] ➡️ No role, redirecting to /onboarding",
          );
          navigate("/onboarding", { replace: true });
        }
      } else {
        // Есть роль - если находимся на служебных страницах, редиректим на главную
        if (
          ["/onboarding", "/auth-error", "/role-selection"].includes(
            location.pathname,
          )
        ) {
          console.log(
            "[AuthController] ➡️ Has role, redirecting from service page to home",
          );
          navigate("/", { replace: true });
        }
      }
      return;
    }

    // Если есть сессия но нет профиля (новый пользователь) - даем время на загрузку
    if (session && !userProfile) {
      console.log(
        "[AuthController] 📋 Session exists but profile not loaded yet",
        { authStatus },
      );
      // Профиль загружается, ждем
      return;
    }

    // Если нет сессии и не загружается - показываем обычный контент (AuthHandler решит что делать)
    console.log(
      "[AuthController] 👤 No session, letting AuthHandler manage flow",
      { authStatus },
    );
  }, [
    session,
    userProfile,
    appLoading,
    authError,
    authStatus,
    navigate,
    location.pathname,
    isPublicRoute,
  ]);

  // Показываем loading только если действительно загружаем данные
  if (appLoading && !isPublicRoute) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen gap-3">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="text-sm text-muted-foreground">Завантаження...</p>
      </div>
    );
  }

  return <>{children}</>;
};

// -------------------------------------------
// MockBootTracer - forces auth attempt in mock when idle
// -------------------------------------------
const MockBootTracer: React.FC = () => {
  const { webApp, isReady } = useTelegram();
  const {
    authStatus,
    isAuthenticationAttempted,
    isAuthenticating,
    authenticateWithTelegram,
  } = useAuth();
  useEffect(() => {
    if (!isTelegramMockEnabled()) return;
    const timer = setTimeout(async () => {
      if (
        !isAuthenticationAttempted &&
        !isAuthenticating &&
        authStatus !== "authenticated" &&
        authStatus !== "error"
      ) {
        if (webApp?.initData && isReady) {
          console.log("[MockBootTracer] Forcing auth attempt (fallback)");
          try {
            await authenticateWithTelegram(webApp.initData);
          } catch (e) {
            console.warn("[MockBootTracer] force auth error", e);
          }
        } else {
          console.log("[MockBootTracer] Conditions not met for force attempt", {
            hasInitData: !!webApp?.initData,
            isReady,
          });
        }
      }
    }, MOCK_FORCE_AUTH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [
    webApp,
    isReady,
    authStatus,
    isAuthenticationAttempted,
    isAuthenticating,
    authenticateWithTelegram,
  ]);
  return null;
};

// -------------------------------------------
// DevAuthDebug - diagnostic overlay (dev + mock only)
// -------------------------------------------
const DevAuthDebug: React.FC = () => {
  const {
    authStatus,
    session,
    userProfile,
    isAuthReady,
    isAuthenticating,
    isLoading,
    isAuthenticationAttempted,
  } = useAuth();
  const { webApp } = useTelegram();
  if (!import.meta.env.DEV || !isTelegramMockEnabled()) return null;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none text-[10px] px-2 py-1 rounded bg-gray-100/90 text-gray-900 font-mono space-x-2 fixed bottom-2 left-2 z-[9999] border border-gray-300"
      style={{
        backgroundColor: "rgba(243, 244, 246, 0.9)",
        color: "#1F2937",
        borderColor: "#D1D5DB",
      }}
    >
      <span>auth:{authStatus}</span>
      <span>ready:{String(isAuthReady)}</span>
      <span>load:{String(isLoading)}</span>
      <span>authing:{String(isAuthenticating)}</span>
      <span>sess:{session ? "1" : "0"}</span>
      <span>role:{userProfile ? (userProfile.role ?? "null") : "-"}</span>
      <span>attempted:{String(isAuthenticationAttempted)}</span>
      <span>initData:{webApp?.initData ? "Y" : "N"}</span>
    </div>
  );
};

// -------------------------------------------
// LayoutWrapper - conditionally renders sidebar/header based on route
// Use a function declaration to ensure robust hoisting across builds/HMR
// -------------------------------------------
function LayoutWrapper(): JSX.Element {
  const location = useLocation();

  // Список роутів, де НЕ потрібен sidebar та header (auth flow сторінки)
  const noLayoutRoutes = ["/role-selection", "/onboarding", "/auth-error"];
  const shouldHideLayout = noLayoutRoutes.includes(location.pathname);

  if (shouldHideLayout) {
    // Рендеримо тільки контент без sidebar та header
    return <AppRoutes />;
  }

  // Стандартний layout з sidebar та header
  return (
    <SidebarShell>
      <PrimarySidebar />
      <SidebarInset>
        <PrimaryHeader />
        <AppRoutes />
      </SidebarInset>
    </SidebarShell>
  );
}

// -------------------------------------------
// Root App Component
// -------------------------------------------
const App = () => {
  // Автоматическая синхронизация языка из Telegram с i18n
  useLanguageSync();

  // User presence tracking for Telegram notifications
  useUserPresence();

  // Глобальная обработка ошибок динамического импорта Vite с улучшенной логикой retry
  useEffect(() => {
    const RETRY_KEY = "vite_chunk_reload_count";
    const RETRY_TIMESTAMP_KEY = "vite_chunk_last_reload";
    const MAX_RETRIES = 2;
    const RETRY_COOLDOWN = 10000; // 10 секунд
    const failedModules = new Set<string>();

    const clearAllCaches = async () => {
      // Очищаем HTTP кеш
      if ("caches" in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
          console.log("[Vite] Cleared", cacheNames.length, "cache(s)");
        } catch (err) {
          console.warn("[Vite] Failed to clear caches:", err);
        }
      }

      // Очищаем Service Worker если є
      if ("serviceWorker" in navigator) {
        try {
          const registrations =
            await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((reg) => reg.unregister()));
          if (registrations.length > 0) {
            console.log(
              "[Vite] Unregistered",
              registrations.length,
              "service worker(s)",
            );
          }
        } catch (err) {
          console.warn("[Vite] Failed to unregister service workers:", err);
        }
      }
    };

    const handlePreloadError = async (event: Event) => {
      const customEvent = event as CustomEvent<{ url?: string }>;
      const payload = customEvent.detail || {};
      const failedUrl = payload.url || "unknown";

      failedModules.add(failedUrl);

      console.error("[Vite Preload Error]", {
        url: failedUrl,
        totalFailed: failedModules.size,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        location: window.location.href,
      });

      // Предотвращаем стандартное поведение
      event.preventDefault();

      try {
        const retryCount = parseInt(
          sessionStorage.getItem(RETRY_KEY) || "0",
          10,
        );
        const lastReload = parseInt(
          sessionStorage.getItem(RETRY_TIMESTAMP_KEY) || "0",
          10,
        );
        const now = Date.now();

        // Проверяем cooldown
        if (now - lastReload < RETRY_COOLDOWN) {
          console.warn("[Vite] Retry cooldown active, skipping auto-reload");
          return;
        }

        if (retryCount < MAX_RETRIES) {
          console.log(
            `[Vite] Auto-reloading (attempt ${retryCount + 1}/${MAX_RETRIES})`,
          );

          // Очищаем все кеши
          await clearAllCaches();

          // Обновляем счетчики
          sessionStorage.setItem(RETRY_KEY, String(retryCount + 1));
          sessionStorage.setItem(RETRY_TIMESTAMP_KEY, String(now));

          // Задержка для показа сообщения
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          console.error(
            "[Vite] Max reload attempts reached. Failed modules:",
            Array.from(failedModules),
          );

          // Показываем пользователю диалог
          const shouldReload = window.confirm(
            "Failed to load application components. A new version may be available.\n\nReload the page now?",
          );

          if (shouldReload) {
            // Очищаем счетчики и кеши
            sessionStorage.clear();
            await clearAllCaches();
            window.location.reload();
          }
        }
      } catch (err) {
        console.error("[Vite] Error during preload error handling:", err);
      }
    };

    window.addEventListener("vite:preloadError", handlePreloadError);
    return () =>
      window.removeEventListener("vite:preloadError", handlePreloadError);
  }, []);

  useEffect(() => {
    let fired = false;
    const run = () => {
      if (fired) return;
      fired = true;
      // Defer to next tick so that immediate interactions don't compete with hydration
      setTimeout(() => {
        initPostHog();
      }, 0);
      window.removeEventListener("pointerdown", run);
      window.removeEventListener("keydown", run);
    };
    if ("requestIdleCallback" in window) {
      (
        window as unknown as {
          requestIdleCallback: (
            cb: () => void,
            opts?: { timeout?: number },
          ) => void;
        }
      ).requestIdleCallback(() => run(), { timeout: 2500 });
    } else {
      setTimeout(run, 1500);
    }
    window.addEventListener("pointerdown", run, { once: true });
    window.addEventListener("keydown", run, { once: true });
    return () => {
      window.removeEventListener("pointerdown", run);
      window.removeEventListener("keydown", run);
    };
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <HeaderProvider>
        <ConfigurationProvider>
          <TurnstileProvider>
            <TelegramProvider>
              <TelegramPlatformGuard>
                <AuthProvider>
                  <BrowserRouter
                    future={{
                      v7_startTransition: true,
                      v7_relativeSplatPath: true,
                    }}
                  >
                    <TooltipProvider>
                      <Toaster />
                      <Sonner />
                      <RouteErrorBoundary>
                        {/* Global suspense now only protects initial critical providers; individual routes have granular fallbacks */}
                        <Suspense
                          fallback={
                            <div className="flex items-center justify-center w-full h-screen text-sm">
                              Bootstrapping...
                            </div>
                          }
                        >
                          <MockBootTracer />
                          <AuthHandler />
                          <AuthController>
                            <LayoutWrapper />
                            <DevAuthDebug />
                            {/* PWA Reload Prompt - показывается при доступности новой версии */}
                            <ErrorBoundary fallback={null}>
                              <Suspense fallback={null}>
                                {/* <ReloadPrompt /> */}{" "}
                                {/* Disabled: vite-plugin-pwa removed */}
                              </Suspense>
                            </ErrorBoundary>
                            {/* Push Notification Prompt - показывается после успешной авторизации */}
                            {/* Обернут в ErrorBoundary для изоляции ошибок загрузки */}
                            <ErrorBoundary fallback={null}>
                              <Suspense fallback={null}>
                                <PushNotificationPrompt />
                              </Suspense>
                            </ErrorBoundary>
                          </AuthController>
                        </Suspense>
                      </RouteErrorBoundary>
                    </TooltipProvider>
                  </BrowserRouter>
                </AuthProvider>
              </TelegramPlatformGuard>
            </TelegramProvider>
          </TurnstileProvider>
        </ConfigurationProvider>
      </HeaderProvider>
    </QueryClientProvider>
  );
};
export default App;
